import { ByocUploadContentService } from './../../service/upload-content.service';
import { BroadcastService } from '@app/core/service';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';

import { ByocEpisodeService } from '../../service/episode.service';
import { BehaviorSubject, distinctUntilChanged, map, Subject, Subscription, switchMap, takeUntil, tap } from 'rxjs';
import { ByocService } from '../../../service/byoc.service';
import { Episode } from '@app/core/model/library.model';
import { AffectedService } from './services/affected.service';

@Component({
    selector: 'app-byoc-episode-step-users',
    templateUrl: './users.component.html',
    styleUrls: ['./users.component.scss'],
    // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- legacy component, migrate to OnPush when touched
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [AffectedService],
    standalone: false,
})
export class ByocEpisodeStepUsersComponent implements OnInit, OnDestroy {
    private changeDetectorRef = inject(ChangeDetectorRef);
    private broadcastService = inject(BroadcastService);
    private byocService = inject(ByocService);
    private byocEpisodeService = inject(ByocEpisodeService);
    private affectedService = inject(AffectedService);
    private readonly byocUploadContentService = inject(ByocUploadContentService);

    saving$ = new BehaviorSubject<boolean | null>(null);
    loaded$ = this.affectedService.affected$.pipe(
        map((affected) => affected.loaded),
        distinctUntilChanged(),
    );

    missionNames$ = this.affectedService.affected$.pipe(map((affected) => (affected.content.missions ?? []).map((mission) => mission.name).join(', ')));

    courseName$ = this.affectedService.affected$.pipe(map((affected) => affected.content.course?.name ?? ''));

    count$ = this.affectedService.affected$.pipe(map((affected) => affected.users.lazyload.totalItems));
    reuploadLoading$ = this.byocUploadContentService.reuploadLoading$;

    private readonly subscriptions: Subscription = new Subscription();

    ngOnInit() {
        this.affectedService.setAffectedLoaded(false);

        const stop$ = new Subject<null>();

        this.subscriptions.add(
            this.byocService
                .episodeAffectedUsers(
                    <string>this.byocEpisodeService.form.value.courseId,
                    this.affectedService.affected.users.lazyload.limit,
                    this.affectedService.affected.users.lazyload.offset.suffixPosition,
                )
                .pipe(
                    takeUntil(stop$),
                    tap((data) => {
                        if (data.pagination.totalItems === 0) {
                            if (this.canEditEpisodeAndUploadNewContent) {
                                this.reuploadAndPublish();
                                stop$.next(null);
                                return;
                            } else {
                                this.publishClick();
                                stop$.next(null);
                                return;
                            }
                        }

                        this.affectedService.setAffectedUsers(data.users, {
                            total: data.pagination.totalItems,
                            suffixPosition: data.users.length,
                        });
                    }),
                    switchMap(() => this.byocService.episodeAffectedContent(this.byocEpisodeService.value.courseId)),
                    tap((content) => {
                        const [course, missions] = content;
                        this.affectedService.setAffectedContent({ course, missions });
                    }),
                    tap(() => this.affectedService.setAffectedLoaded(true)),
                )
                .subscribe(),
        );
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    get canEditEpisodeAndUploadNewContent() {
        return (
            this.byocUploadContentService.uploadType &&
            this.byocUploadContentService.uploadType?.length > 0 &&
            this.byocUploadContentService.episodeToEditId &&
            this.byocUploadContentService.episodeToEditId?.length > 0
        );
    }

    /**
     * Back to previous step
     */
    backClick(): void {
        if (this.byocEpisodeService.skipSteps.includes('course')) {
            this.byocEpisodeService.step = 'general';
        } else {
            this.byocEpisodeService.step = 'course';
        }
        this.changeDetectorRef.markForCheck();
    }

    /**
     * Save
     */
    publishClick(): void {
        if (this.saving$.value) {
            return;
        }

        this.saving$.next(true);

        this.byocEpisodeService.affectedUsers = this.affectedService.affected.users.lazyload.totalItems;

        this.subscriptions.add(
            this.byocService
                .createEpisode(this.byocEpisodeService.getEpisodeDataToSent())
                .pipe(
                    tap((episode: Episode) => {
                        this.byocEpisodeService.episode = episode;
                        this.byocEpisodeService.step = this.byocUploadContentService.uploadType ? 'uploaded-content-info' : 'recommend';
                        this.changeDetectorRef.markForCheck();
                        this.broadcastService.send('added-new-li-popup');
                    }),
                )
                .subscribe(),
        );
    }

    reuploadAndPublish() {
        this.byocUploadContentService.editEpisodeAndUploadNewContent();
    }
}
